/**
 * giftCardsApi — client for `/api/v1/gift-cards` (BON-223 contract).
 *
 * Success bodies are wrapped (`{ giftCards, pagination }` / `{ giftCard }`),
 * matching the v1 customers/bookings convention this endpoint mirrors.
 * Staff can also issue a card with `createGiftCard`.
 */
import { apiFetch } from '../../lib/apiFetch';

import type {
  CreateGiftCardBody,
  CreateGiftCardResponse,
  GiftCardDetail,
  GiftCardDetailResponse,
  GiftCardListResponse,
  GiftCardSourceFilter,
  GiftCardStatusFilter,
} from './giftCardTypes';

const BASE = '/api/v1/gift-cards';

export interface FetchGiftCardsParams {
  search?: string;
  status?: GiftCardStatusFilter;
  source?: GiftCardSourceFilter;
  limit?: number;
  offset?: number;
}

/** The server's message when it sent one, else copy keyed to the status. */
async function errorFrom(res: Response, what: string): Promise<Error> {
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  const fallback =
    res.status === 403
      ? "You don't have access to gift cards."
      : res.status === 404
        ? 'That gift card no longer exists.'
        : `${what} (${res.status})`;
  return new Error(body.message || fallback);
}

export async function fetchGiftCards(
  params: FetchGiftCardsParams = {},
  signal?: AbortSignal,
): Promise<GiftCardListResponse> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  // 'all' is the FE-only sentinel for "no filter" — omitted rather than sent,
  // since the API 400s an unrecognised status/source value.
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.source && params.source !== 'all') qs.set('source', params.source);
  if (params.limit) qs.set('limit', params.limit.toString());
  if (params.offset !== undefined) qs.set('offset', params.offset.toString());

  const res = await apiFetch(`${BASE}?${qs.toString()}`, signal ? { signal } : undefined);
  if (!res.ok) throw await errorFrom(res, 'Failed to load gift cards');
  return (await res.json()) as GiftCardListResponse;
}

export async function fetchGiftCardDetail(
  id: string,
  signal?: AbortSignal,
): Promise<GiftCardDetail> {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}`, signal ? { signal } : undefined);
  if (!res.ok) throw await errorFrom(res, 'Failed to load this gift card');
  const body = (await res.json()) as GiftCardDetailResponse;
  return body.giftCard;
}

/** Carries the HTTP status so the create form can word each refusal differently. */
export class CreateGiftCardError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'CreateGiftCardError';
  }
}

export async function createGiftCard(body: CreateGiftCardBody): Promise<CreateGiftCardResponse> {
  const res = await apiFetch(BASE, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new CreateGiftCardError(
      err.message ?? `Failed to create gift card (${res.status})`,
      res.status,
    );
  }
  return (await res.json()) as CreateGiftCardResponse;
}
